// Form fields in the prototype style (O1 invite / sign in, O11 save).
//   <TextField label="Email" type="email" value={email} onInput={setEmail} placeholder="you@email.com" />
//   <SwitchRow title="Let Margo send reminders" sub="..." checked={on} onChange={setOn} />
import type { ComponentChildren, JSX } from 'preact';
import { useMemo } from 'preact/hooks';

let uid = 0;

export interface TextFieldProps {
  label: string;
  value: string;
  onInput: (v: string) => void;
  type?: 'text' | 'email' | 'password';
  placeholder?: string;
  /** 'mono' = Space Mono 18px 700, letter-spacing 0.08em (invite code). */
  variant?: 'text' | 'mono';
  /** Input height (default 50; the invite code uses 54). */
  h?: number;
  autoComplete?: string;
  autoCapitalize?: 'off' | 'none' | 'characters' | 'words' | 'sentences';
  inputMode?: 'text' | 'email';
  enterKeyHint?: 'go' | 'next' | 'done' | 'send';
  onEnter?: () => void;
  invalid?: boolean;
  /** Error/help text under the field, linked with aria-describedby. */
  hint?: ComponentChildren;
  maxLength?: number;
  autoFocus?: boolean;
  labelStyle?: JSX.CSSProperties;
}

export function TextField(p: TextFieldProps) {
  const id = useMemo(() => 'xm-f' + (++uid), []);
  const h = p.h ?? 50;
  return (
    <>
      <label for={id} style={{ fontSize: 13, fontWeight: 700, color: '#CFC8BC', ...p.labelStyle }}>{p.label}</label>
      <input
        id={id} type={p.type || 'text'} value={p.value} placeholder={p.placeholder}
        autoComplete={p.autoComplete} autoCapitalize={p.autoCapitalize} inputMode={p.inputMode} enterKeyHint={p.enterKeyHint}
        // Never pass maxLength={undefined}: Preact would set input.maxLength = '' (= 0) on a reused input.
        spellcheck={false} maxLength={p.maxLength ?? 320} autoFocus={p.autoFocus}
        aria-invalid={p.invalid ? 'true' : undefined} aria-describedby={p.hint ? id + '-hint' : undefined}
        onInput={(e) => p.onInput((e.currentTarget as HTMLInputElement).value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && p.onEnter) { e.preventDefault(); p.onEnter(); } }}
        style={{
          height: h, width: '100%', padding: '0 16px', borderRadius: h >= 54 ? 16 : 14,
          border: `1px solid ${p.invalid ? '#F28B82' : 'rgba(255,255,255,0.2)'}`, background: 'rgba(245,241,234,0.06)', color: '#F5F1EA',
          font: p.variant === 'mono' ? "700 18px 'Space Mono', monospace" : "500 16px 'Figtree', sans-serif",
          letterSpacing: p.variant === 'mono' ? '0.08em' : undefined, outline: 'none',
        }}
      />
      {p.hint && <p id={id + '-hint'} role={p.invalid ? 'alert' : undefined} style={{ margin: 0, fontSize: 13.5, lineHeight: 1.4, color: p.invalid ? '#F28B82' : '#B9B2A6' }}>{p.hint}</p>}
    </>
  );
}

export interface SwitchRowProps {
  title: ComponentChildren;
  sub?: ComponentChildren;
  checked: boolean;
  onChange: (on: boolean) => void;
  style?: JSX.CSSProperties;
}

export function SwitchRow({ title, sub, checked, onChange, style }: SwitchRowProps) {
  return (
    <button
      type="button" role="switch" aria-checked={checked ? 'true' : 'false'} onClick={() => onChange(!checked)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, minHeight: 68, padding: '10px 14px', borderRadius: 16, width: '100%',
        border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(245,241,234,0.05)', color: '#F5F1EA', textAlign: 'left', ...style,
      }}
    >
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>{title}</span>
        {sub && <span style={{ fontSize: 13, lineHeight: 1.35, color: '#CFC8BC' }}>{sub}</span>}
      </span>
      <span aria-hidden="true" style={{ flex: 'none', width: 48, height: 28, borderRadius: 14, background: checked ? 'var(--acc)' : 'rgba(255,255,255,0.2)', position: 'relative', transition: 'background 200ms ease' }}>
        <span style={{ position: 'absolute', top: 3, left: checked ? 23 : 3, width: 22, height: 22, borderRadius: 11, background: '#F5F1EA', transition: 'left 200ms ease' }} />
      </span>
    </button>
  );
}
